import React, { useEffect, useState } from "react";
import { fetchGiftInbox, claimGift } from "@/lib/api";
import { Gift, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Small floating banner on the Dashboard listing any pending gifts.
 * Auto-claims after user hits "Accept".
 */
export default function GiftInbox({ sections, onClaimed }) {
  const [gifts, setGifts] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const refresh = async () => {
    try {
      const g = await fetchGiftInbox();
      setGifts(g);
    } catch (e) {
      console.error("gift inbox fetch failed", e);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const accept = async (id) => {
    setBusyId(id);
    try {
      await claimGift(id);
      toast.success("A chapter has just opened for you.");
      await refresh();
      if (onClaimed) onClaimed();
    } catch (e) {
      toast.error("Could not accept the gift.");
    } finally {
      setBusyId(null);
    }
  };

  if (!gifts.length) return null;

  return (
    <div className="mb-12 space-y-3" data-testid="gift-inbox">
      {gifts.map((g) => {
        const sec = sections.find((s) => s.id === g.section_id);
        return (
          <div
            key={g.id}
            className="border border-terracotta/50 bg-terracotta/5 p-5 flex items-start gap-4 animate-fade-up"
            data-testid={`gift-inbox-${g.id}`}
          >
            <div className="h-9 w-9 border border-terracotta text-terracotta flex items-center justify-center shrink-0">
              <Gift className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">
                A gift from {g.from_name || "a friend"}
              </p>
              <p className="font-serif text-lg text-bone-100 leading-snug mb-1">
                <em className="italic">{sec?.title || "A chapter"}</em> is waiting for you.
              </p>
              {g.note && (
                <p className="font-serif text-sm text-bone-300 italic leading-relaxed mt-2">
                  "{g.note}"
                </p>
              )}
            </div>
            <button
              onClick={() => accept(g.id)}
              disabled={busyId === g.id}
              data-testid={`accept-gift-${g.id}`}
              className="shrink-0 border border-terracotta bg-terracotta hover:bg-terracotta-deep text-bone-100 px-4 py-2 font-sans text-xs uppercase tracking-[0.2em] transition-colors flex items-center gap-2 disabled:opacity-60"
            >
              {busyId === g.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              Accept
            </button>
          </div>
        );
      })}
    </div>
  );
}
